import React, { useState, useEffect } from 'react';
import { Container } from 'semantic-ui-react';
import GoogleAnalytics from 'react-ga';
import { useDispatch } from 'react-redux';
import { Media } from '../../../mediaSetting';
import { setSecondsToAutoRefresh, tryReconnectPubNub } from 'actions/networkHealthStatus';
import { getUserBasicProfile } from 'actions/user';
import { getMeetingRoomsByPage } from 'actions/meetingRoom';
import { getWaitingAreasDetails } from 'businessLogic/pubnubHelpers';
import { getEntitiesWithSearchTerm, updateCurrentActivePageNo } from 'actions/organisation';
import { queueESEvents, ESEventTypes } from 'businessLogic/logEventToElasticSearch';
import HtmlToReact from 'html-to-react';
import xss from 'xss';

let htmlToReactParser = new HtmlToReact.Parser(React);

export default function PubnubWarningMessageContent(props) {
  const dispatch = useDispatch();
  const [mountTime, setMountTime] = useState(null);

  useEffect(() => {
    setMountTime(new Date());
    // track how many times this component/message is shown via Google Analytics
    // TODO - Log this into New Relic instead (after 3.9.1 is out)
    window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
      GoogleAnalytics.event({
        category: 'VCM',
        action: 'PubnubWarningMessage componentDidMount',
      });
    queueESEvents(ESEventTypes.PubnubWarningMessageMount, true, 'eventType');

    return () => {
      const currentTime = new Date();
      const secondsSinceMount = Math.floor((currentTime - mountTime) / 1000);

      // track how many times this component/message recovers/self-closes via Google Analytics
      // TODO - Log this into New Relic instead (after 3.9.1 is out)
      window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
        GoogleAnalytics.event({
          category: 'VCM',
          action: 'PubnubWarningMessage componentWillUnmount',
          label: `${secondsSinceMount} sec`,
        });

      queueESEvents(ESEventTypes.PubnubWarningMessageUnmount, `${secondsSinceMount} sec`, 'eventType');
    };
  }, []);

  // Event handler for Refresh Now button
  const onRefreshNowClicked = () => {
    dispatch(setSecondsToAutoRefresh(0));
    dispatch(getUserBasicProfile());
    let cb = (moreEntitiesReturned) => {
      if (moreEntitiesReturned) {
        // Once data fetch finishes and fetched meetingrooms.length > 0, the active page no needs to be increased by 1
        dispatch(updateCurrentActivePageNo(1));
      }
    };

    // re-fetch all entities
    if (props.userProfile.hasWaitingAreas) {
      queueESEvents(
        ESEventTypes.CallingWADetails,
        `When clicking pubnub yellow msg`,
        'eventType&eventValue',
        null,
        null,
        true,
        'info'
      );
      getWaitingAreasDetails(dispatch);
    }
    if (props.userProfile.hasMeetingRooms) dispatch(getMeetingRoomsByPage());
    if (props.userProfile.hasOrganisations) dispatch(getEntitiesWithSearchTerm(null, null, cb));

    tryReconnectPubNub();

    const currentTime = new Date();
    const secondsSinceMount = Math.floor((currentTime - mountTime) / 1000);
    // track how many times user clicks [refresh now] via Google Analytics
    // TODO - Log this into New Relic instead (after 3.9.1 is out)
    window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
      GoogleAnalytics.event({
        category: 'VCM',
        action: 'PubnubWarningMessage onRefreshNowClicked',
        label: `${secondsSinceMount} sec`,
      });

    queueESEvents(ESEventTypes.PubnubWarningRefreshBtnClick, `${secondsSinceMount} sec`, 'eventType');
  };

  const isRefreshingNow = !props.isPubNubHealthy && props.pubnubConnectionStatus.secondsToAutoRefresh === 0;

  let messageClasses =
    'pubnub-warning-message ui message txt-size-m pdm box-shadow-null txt-centered color-warning-things-bg';

  if (props.isPubNubHealthy) {
    messageClasses += ' hidden';
  }

  // Prepare the messages to display
  let messageNonMobile = APP.LANGUAGE.PubnubNotAvailable.RefreshingNowMessage;
  let messageMobile = APP.LANGUAGE.PubnubNotAvailable.RefreshingNowMessage;
  if (props.pubnubConnectionStatus.secondsToAutoRefresh > 1) {
    messageNonMobile = APP.LANGUAGE.PubnubNotAvailable.GoingToRefreshMessageNonMobile.replace(
      '##numberOfSec##',
      props.pubnubConnectionStatus.secondsToAutoRefresh
    ).replace('##termSeconds##', 'seconds');
    messageMobile = APP.LANGUAGE.PubnubNotAvailable.GoingToRefreshMessageMobile.replace(
      '##numberOfSec##',
      props.pubnubConnectionStatus.secondsToAutoRefresh
    ).replace('##termSeconds##', 'seconds');
  } else if (props.pubnubConnectionStatus.secondsToAutoRefresh === 1) {
    messageNonMobile = APP.LANGUAGE.PubnubNotAvailable.GoingToRefreshMessageNonMobile.replace(
      '##numberOfSec##',
      props.pubnubConnectionStatus.secondsToAutoRefresh
    ).replace('##termSeconds##', 'second');
    messageMobile = APP.LANGUAGE.PubnubNotAvailable.GoingToRefreshMessageMobile.replace(
      '##numberOfSec##',
      props.pubnubConnectionStatus.secondsToAutoRefresh
    ).replace('##termSeconds##', 'second');
  }

  return (
    <div className="color-warning-things-bg">
      {/* Layout for Tablet and Desktop */}
      <Container fluid as={Media} greaterThanOrEqual="tablet" className={messageClasses}>
        <span className="text-size-m" dangerouslySetInnerHTML={{ __html: messageNonMobile }} />
        <div className="ui labeled button arrow">
          <div className="ui small  mrls hand button refresh-now-btn text-size-m" onClick={onRefreshNowClicked}>
            {APP.LANGUAGE.PubnubNotAvailable.RefreshNowButtonTextNonMobile}
          </div>
        </div>
      </Container>
      {/* Layout for Mobile */}
      <Container fluid as={Media} at="mobile" className={messageClasses + ' inline-flex center-flex space-between'}>
        <span
          className={`text-size-m fl ${isRefreshingNow ? 'pdts' : ''}`}
          dangerouslySetInnerHTML={{ __html: messageMobile }}
        />
        <div>
          <div className="ui labeled button arrow mrrnull">
            <div className="ui button refresh-now-btn" onClick={onRefreshNowClicked}>
              <i className="refresh icon mrrnull" />
              {APP.LANGUAGE.PubnubNotAvailable.RefreshNowButtonTextMobile}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}
